import sanitizeHtml from "sanitize-html";

import { DEFAULT_PAGINATOR_SIZES, ROLE, WIDTH_NG } from "~/assets/js/constants";
import { createPaginator, isAllowedTo } from "~/assets/js/utils";

export const toNewsItem = (item) => ({
  id: item.id,
  headline: item.headline,
  body: item.body,
  byEmail: item.byEmail,
  createdOn: item.createdOn,
  updatedOn: item.updatedOn,
  readByUser: item.readByUser,
  vaccine: item.vaccine,
});

export const toNewsItemList = (items = []) => items.map(toNewsItem);

const TABLE_MAIN = "headline";

const HEADER_SIZES = [WIDTH_NG.XS, WIDTH_NG["4XL"], WIDTH_NG.MD, 38];

function toNewsItemRow(newsItem, { role, i18n }) {
  const { id, headline, body, byEmail, createdOn, vaccine } = newsItem;

  const actions = [];

  if (isAllowedTo([ROLE.ADMIN, ROLE.SERVICEDESK], role) && !byEmail) {
    actions.push(
      {
        active: false,
        id: "newsitem-edit",
        icon: "s2-icon-pen-regular",
        label: i18n.t("generic.edit"),
        loading: false,
      },
      {
        active: false,
        id: "newsitem-delete",
        icon: "s2-icon-trash-regular",
        label: i18n.t("generic.delete"),
        loading: false,
      },
    );
  }

  const columns = [
    { id: "headline", type: "text", data: { label: headline } },
    {
      id: "body",
      type: "text",
      data: {
        label: sanitizeHtml(body, { allowedTags: [], allowedAttributes: {} }),
      },
    },
    {
      id: "byEmail",
      type: "text",
      data: { label: byEmail ? i18n.t("generic.yes") : i18n.t("generic.no") },
    },
    {
      id: "createdOn",
      type: "text",
      data: { label: i18n.d(new Date(createdOn * 1000), "medium") },
    },
  ];

  return { id, headline, body, byEmail, createdOn, vaccine, actions, columns };
}

const LATEST_NEWS_HEADER_SIZES = [WIDTH_NG.XS, WIDTH_NG["4XL"], WIDTH_NG.MD, 38, 38];

function toReadState(readByUser) {
  return readByUser
    ? { icon: "s2-icon-check-solid", state: "ok" }
    : { icon: "s2-icon-eye-solid", state: "ok" };
}

function toLatestNewsRow(newsItem, { i18n }) {
  const { id, headline, body, byEmail, createdOn, updatedOn, readByUser, vaccine } = newsItem;

  const columns = [
    { id: "headline", type: "text", data: { label: headline } },
    {
      id: "body",
      type: "text",
      data: { label: sanitizeHtml(body, { allowedTags: [], allowedAttributes: {} }) },
    },
    {
      id: "byEmail",
      type: "text",
      data: { label: byEmail ? i18n.t("generic.yes") : i18n.t("generic.no") },
    },
    { id: "createdOn", type: "text", data: { label: i18n.d(new Date(createdOn * 1000)) } },
    { id: "updatedOn", type: "text", data: { label: i18n.d(new Date(updatedOn * 1000)) } },
  ];

  return {
    id,
    headline,
    body,
    byEmail,
    createdOn,
    updatedOn,
    readByUser,
    vaccine,
    actions: [],
    columns,
    ...toReadState(readByUser),
  };
}

export const toLatestNewsTable = (newsItems, { i18n }) => {
  const headers = [
    { id: "headline", label: i18n.t("news.headers.headline"), sortable: false },
    { id: "body", label: i18n.t("news.headers.body"), sortable: false },
    { id: "byEmail", label: i18n.t("news.headers.byEmail"), sortable: false },
    { id: "createdOn", label: i18n.t("news.headers.createdOn"), sortable: false },
    { id: "updatedOn", label: i18n.t("news.headers.updatedOn"), sortable: false },
  ];

  return {
    main: TABLE_MAIN,
    sizes: LATEST_NEWS_HEADER_SIZES,
    headers,
    rows: newsItems.collection.map((item) => toLatestNewsRow(item, { i18n })),
  };
};

export const toNewsItemTable = (newsItems, { role, i18n }) => {
  const paginator = createPaginator(newsItems, i18n, DEFAULT_PAGINATOR_SIZES);

  const headers = [
    { id: "headline", label: i18n.t("news.headers.headline"), sortable: true },
    { id: "body", label: i18n.t("news.headers.body"), sortable: true },
    { id: "byEmail", label: i18n.t("news.headers.byEmail"), sortable: true },
    { id: "createdOn", label: i18n.t("news.headers.createdOn"), sortable: true },
  ];

  return {
    paginator,
    sizes: HEADER_SIZES,
    main: TABLE_MAIN,
    headers,
    rows: newsItems.collection.map((item) => toNewsItemRow(item, { role, i18n })),
  };
};
